import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import './CheckoutSheet.scss'
import SheetButton from '../SheetButton/SheetButton'
import AddressForm from 'components/AddressForm/AddressForm'
import {
  useAddressStore,
  useTilesStore,
  usePhotoBooksStore,
} from '../../stores/RootStoreProvider'
import MixtilesDialog from '../MixtilesDialog/MixtilesDialog'
import PaymentManager from '../../services/PaymentManager'
import {
  initiatePaypalPayment,
  isReusablePaymentMethod,
  isPaymentMethodGoogleOrApple,
  shouldTreatAsStalePaymentMethod,
} from '../../stores/paymentStore/payment.utils'
import { PaymentMethodTypeDisplay } from '../../stores/paymentStore/payment.consts'
import { PaymentMethodType } from '../../services/PaymentManager.types'
import OrderSummary from './OrderSummary/OrderSummary'
import { giftNoteManager } from '../../services/GiftNoteManager'
import {
  SHIPPING_OPTION_EXPRESS,
  SHIPPING_OPTION_FREE,
  SHIPPING_OPTION_PRIORITY,
} from '../../stores/shippingStore/shipping.consts'
import {
  getDeliveryDateDescription,
  getShippingOptionPricing,
} from '../../stores/shippingStore/shipping.utils'
import BasicDialogContent from '../MixtilesDialog/BasicDialogContent/BasicDialogContent'
import BasicDialogButton from '../MixtilesDialog/BasicDialogButton/BasicDialogButton'
import { analytics, EVENT_NAMES } from '../../services/Analytics/Analytics'
import { formatPrice } from '../../utils/utils'
import { Elements } from '@stripe/react-stripe-js'
import { type Stripe } from '@stripe/stripe-js'
import { inGdprCountry } from '../../utils/GDPR.utils'
import { translateManager as t } from '../../services/TranslateManager'

import StripePaymentRequestForm from 'components/StripePaymentRequestForm/StripePaymentRequestForm'
import capitalize from 'lodash/capitalize'
import RadioButton from '../RadioButton/RadioButton'

import UserManager, { CAPTURE_EMAIL_SOURCE } from '../../services/UserManager'
import { countryManager } from '../../services/CountryManager'
import { ADDRESS_SOURCE } from '../../stores/addressStore/address.consts'
import CloseButton from '../TopBar/CloseButton/CloseButton'
import { ReactComponent as CreditCardIcon } from './icons/changeCredit.svg'
import { ReactComponent as AddressIcon } from './icons/addressIcon.svg'
import { ReactComponent as GiftIcon } from './icons/gift.svg'
import Button from 'mixtiles-web-common/ui/Button'
import PayPalButtonWrapper from '../PayPalButtonWrapper/PayPalButtonWrapper'
import { getAuthRequestConfig } from '../../services/LoginUtills'
import { savePaymentConfigApi } from '../../api/paymentConfig.api'
import useApi from '../../api/apiProvider'
import { useProductType } from '../../services/ProductTypesManager'
import { logger } from '../../services/logger'
import BrowserThemeColor from '../ThemeColor/BrowserThemeColor'
import { useTheme } from 'styled-components'
import * as S from './CheckoutSheet.styles'
import StripePaymentForm from '../StripePaymentForm/StripePaymentForm'
import ExpressCheckoutButton from '../StripeExpressCheckout/ExpressCheckoutButton'
import { useExpressCheckoutAvailability } from '../StripeExpressCheckout/ExpressCheckoutAvailabilityProvider'
import {
  toStripeSheetRows,
  PAYMENT_REQUEST_FORMAT,
} from '../Pricing/stripeSheetRows'
import { MATERIAL_TYPES } from '@mixtiles/web-backend-shared'
import AlertPopup from '../AlertPopup/AlertPopup'
import {
  type CheckoutContextValue,
  useCheckout,
} from '../Checkout/CheckoutContext'
import { useCartStore } from '../../stores/cartStore/CartStoreProvider'
import GiftNoteDialog from '../GiftNoteDialog/GiftNoteDialog'
import { getEncourageExpressShippingForHolidayText } from '../../services/HolidayService'
import {
  getProductArrivalTexts,
  shouldShowSeparateProductArrivals,
} from '../../features/productArrivals/productArrivals.utils'
import { getProductArrivals } from '../../features/productArrivals/cartShippingOptions'
import { getIpLocationAddress } from '../../features/productArrivals/ipLocationAddress'
import { useKeys } from '../../services/KeysProvider'
import { ProductArrivalLines } from '../../features/productArrivals/components/ProductArrivalLines'
import GiftCardEmailForm from './GiftCardEmailForm/GiftCardEmailForm'
import { useAvailableFrames } from '../FrameProvider/AvailableStockProvider'
import { useCheckoutFunnelAnalytics } from '../../features/cart/hooks/useCheckoutFunnelAnalytics'
import { CHECKOUT_ENTRY_POINT } from '../../features/cart/cart.consts'
import { COUNTRIES } from '../../config/countries-data'
import { TOKUSHOHO_URL } from 'config/config'
import { getGiftCardDescription } from './OrderSummary/OrderSummaryUtils'
import { PhotoBookOrderValidation } from '../PhotoBook/PhotoBookProvider/PhotoBook.types'

import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { isGiftCardOrder } from '../../pages/GiftCard/utils'
import { experimentManager } from '../../services/ExperimentManager/ExperimentManager'
import {
  readOutsideReaction,
  readMobxSnapshot,
} from 'utils/mobx/readOutsideReaction'
import {
  getCartBookStores,
  getUnsupportedBagProduct,
} from '../Checkout/unsupportedProductValidation'
import {
  type ShippingOption,
  type ShippingOptionName,
} from '../../stores/shippingStore/shipping.types'
import { type CreateOrderParams } from '../../services/OrderManager.types'

const PHOTO_BOOK_GIFT_NOTE_EMAIL_SUFFIX = '@mixtiles.com'

const FONTS = [{ cssSrc: 'https://use.typekit.net/sgh6suu.css' }] // proxima-nova webfont from Adobe

const SHIPPING_ROW_KEY = 'order.checkout.payment_summary_rows.shipping'
const CALCULATING_DELIVERY_DATE_KEY =
  'order.checkout.shipping_options.calculating_delivery_date'

const translateKey = (key: string, options?: Record<string, unknown>) =>
  t.get(key, options)

type CheckoutSheetProps = {
  dismiss: () => void
  callCreateOrder: (params: Omit<CreateOrderParams, 'bagAnalytics'>) => void
  stripe: Stripe | null
  openCancelPromoCodeDialog: () => void
  isCartFlow: boolean
  rightDrawer: boolean
  desktopReviewElegant: boolean
  paymentRequestData: Awaited<
    ReturnType<typeof PaymentManager.canMakeAppleOrGooglePay>
  > | null
  canMakeApplePay?: boolean
  paymentMethodCheckInitiated?: boolean
}

function CheckoutSheet(props: CheckoutSheetProps) {
  const addressStore = useAddressStore()
  const { ipCountry, ipState } = useKeys()
  const theme = useTheme()
  const { productType } = useProductType()
  const cart = useCartStore()
  const checkout = useCheckout()
  const tilesStore = useTilesStore()
  const photoBooksStore = usePhotoBooksStore()
  const stock = useAvailableFrames()
  const { trackCheckoutStarted, trackCompleteOrderTapped } =
    useCheckoutFunnelAnalytics({ isCartFlow: props.isCartFlow })
  const { isWalletUnavailable } = useExpressCheckoutAvailability()
  const api = useApi()
  const [showAddressDialog, setShowAddressDialog] = useState(false)
  const [showGiftCardEmailDialog, setShowGiftCardEmailDialog] = useState(false)
  const [showPhoneError, setShowPhoneError] = useState(false)
  const [showPaymentElementDialog, setShowPaymentElementDialog] =
    useState(false)
  const [, setShowAddPromoCodeDialog] = useState(false)
  const [showMissingInfoDialog, setShowMissingInfoDialog] = useState(false)
  const [missingInfoMessage, setMissingInfoMessage] = useState<{
    title: string
    text?: string
    onClick?: () => void
  } | null>(null)
  const [transactionId, setTransactionId] = useState<string | null>(null)
  const [checkoutErrorPopupParams, setCheckoutErrorPopupParams] = useState<Omit<
    ComponentProps<typeof AlertPopup>,
    'open'
  > | null>(null)
  const [giftCardBuyerEmail, setGiftCardBuyerEmail] = useState<string | null>(
    () => UserManager.getUserEmail() || null
  )
  const [
    shouldShowPaymentDialogAfterAddress,
    setShouldShowPaymentDialogAfterAddress,
  ] = useState(false)
  const [
    shouldShowPaymentDialogAfterEmail,
    setShouldShowPaymentDialogAfterEmail,
  ] = useState(false)
  const [showGiftNote, setShowGiftNote] = useState(false)
  const [, setAddress] = useState<CheckoutContextValue['address']>()
  const checkoutOpenReported = useRef(false)

  const isGiftCard = productType === PRODUCT_TYPES.GIFT_CARD

  function handleCheckoutError() {
    if (checkout.error && !checkoutErrorPopupParams) {
      analytics.track(EVENT_NAMES.CHECKOUT_PRICING_ERROR_SHOWN, {
        Error: JSON.stringify(checkout.error),
      })
      setCheckoutErrorPopupParams({
        title: t.get(
          'order.checkout.payment_errors.order_price_calculation.title'
        ),
        subtitle: t.get(
          'order.checkout.payment_errors.order_price_calculation.subtitle'
        ),
        buttonText: t.get('general.refresh'),
        onClick: () => {
          setCheckoutErrorPopupParams(null)
          analytics.track(EVENT_NAMES.CHECKOUT_PRICING_ERROR_REFRESH_CLICKED)
          checkout.refreshCheckoutData()
        },
      })
    }
  }

  useEffect(() => {
    if (!checkoutOpenReported.current && !checkout.isLoading) {
      trackCheckoutStarted(CHECKOUT_ENTRY_POINT.CHECKOUT_SHEET)
      checkoutOpenReported.current = true
    }
    handleCheckoutError()
  })

  useLayoutEffect(() => {
    const checkoutSheet = document.querySelector('.checkout-sheet')
    if (checkoutSheet) {
      checkoutSheet.scrollTop = checkoutSheet.scrollHeight
    }
  }, [])

  const openAddressDialog = () => {
    setShowAddressDialog(true)
    analytics.track('Add Address Tapped')
  }

  const handleCloseAddressDialog = (newAddressAdded = false) => {
    const shouldShowPaymentDialog =
      shouldShowPaymentDialogAfterAddress && newAddressAdded
    setShowAddressDialog(false)
    setShouldShowPaymentDialogAfterAddress(false)
    if (shouldShowPaymentDialog) {
      openPaymentDialog()
    }
  }

  const openGiftCardEmailDialog = () => {
    setShowGiftCardEmailDialog(true)
    analytics.track(EVENT_NAMES.GIFT_CARD_ADD_EMAIL_TAPPED)
  }

  const handleCloseGiftCardEmailDialog = (newEmailAdded = false) => {
    const shouldShowPaymentDialog =
      shouldShowPaymentDialogAfterEmail && newEmailAdded
    setShowGiftCardEmailDialog(false)
    setShouldShowPaymentDialogAfterEmail(false)
    if (shouldShowPaymentDialog) {
      openPaymentDialog()
    }
  }

  const openPaymentDialog = () => {
    const { pricingSummary } = checkout
    setShowPaymentElementDialog(true)
    analytics.track('Payment Method Selection Dialog Tapped', {
      'Total Price': pricingSummary.totalPrice,
    })
  }

  const handlePaymentSubmit = async ({
    paymentMethodType,
  }: {
    paymentMethodType: PaymentMethodType
  }) => {
    analytics.track(EVENT_NAMES.PAYMENT_METHOD_SUBMITTED_SUCCESSFULLY, {
      'Payment Method': paymentMethodType,
    })
    if (paymentMethodType === PaymentMethodType.PAYPAL) {
      PaymentManager.selectPayPal()
    } else if (paymentMethodType === PaymentMethodType.APPLE_PAY) {
      PaymentManager.selectApplePay()
    } else if (paymentMethodType === PaymentMethodType.GOOGLE_PAY) {
      PaymentManager.selectGooglePay()
    }
    handleCreditCardDismissed()
    if (api.isAuthenticated && isReusablePaymentMethod(paymentMethodType)) {
      await api.call(savePaymentConfigApi(PaymentManager.getPaymentInfo()))
    }
  }

  const handleCreditCardDismissed = () => {
    setShowPaymentElementDialog(false)
  }

  const openMissingInfoDialog = (
    title: string,
    text?: string,
    onClick: (() => void) | undefined = undefined
  ) => {
    setMissingInfoMessage({
      title,
      text,
      onClick,
    })
    setShowMissingInfoDialog(true)
  }

  const handleCloseMissingInfoDialog = () => {
    if (missingInfoMessage?.onClick) {
      missingInfoMessage?.onClick()
    }
    setShowMissingInfoDialog(false)
  }

  const openPromoCodeDialog = () => {
    setShowAddPromoCodeDialog(true)
  }

  const closePromoCodeDialog = () => {
    setShowAddPromoCodeDialog(false)
  }

  const getOrderedBookStores = () =>
    props.isCartFlow ? getCartBookStores(cart, photoBooksStore) : []

  const handlePlaceOrderClicked = () => {
    placeOrder()

    trackCompleteOrderTapped(
      PaymentManager.getPaymentMethodForOrder(transactionId)?.type
    )
  }

  async function placeOrder() {
    if (!readOutsideReaction(() => validatePlaceOrder(true))) {
      return
    }

    const paymentMethod = PaymentManager.getPaymentMethodForOrder(transactionId)
    const paymentMethodId = PaymentManager.getStripePaymentMethodId()

    const token = await api.getToken()

    // Tiles and photo books are only ordered through the bag, as group orders.
    // Non-group orders are gift card and business purchases: no tiles or books.
    props.callCreateOrder({
      // Tiles
      tiles: checkout.orderTiles,

      // Cart
      orderItems: checkout.orderItems,
      isGroupOrder: checkout.isGroupOrder,
      groupCheckoutId: checkout.groupCheckoutId,

      deliveryPromise: checkout.selectedShippingOption,
      hasUpToDateDeliveryDates: checkout.hasUpToDateDeliveryDates,
      deliveryRange: checkout.getDeliveryRange(checkout.selectedShippingOption),
      address: checkout.address,
      paymentMethod,
      paymentMethodId,
      expressCheckoutConfirmationToken: undefined,
      orderPriceSummary: checkout.pricingSummary,
      shouldValidateAddress: readOutsideReaction(
        () => addressStore.shouldValidateAddressInServer
      ),
      requestConfig: getAuthRequestConfig(token),
      topazLabsEnabled: false,
    })

    props.dismiss()
    setTransactionId(null)
  }

  function validateAddress(validateWithUserInteraction: boolean) {
    const address = readMobxSnapshot(() => addressStore.currentAddress)!
    const tileForJondo = tilesStore.tiles.find((t) =>
      [MATERIAL_TYPES.CANVAS].includes(t.materialType)
    )

    if (!address?.phoneNumber && tileForJondo) {
      if (validateWithUserInteraction) {
        setShowPhoneError(true)
        setShowAddressDialog(true)
      }
      return false
    }

    const unsupportedProduct = props.isCartFlow
      ? getUnsupportedBagProduct({
          cart,
          photoBooksStore,
          tilesStore,
          address,
          localSKUs: stock.localSKUs,
        })
      : null
    if (unsupportedProduct === null) return true

    if (validateWithUserInteraction) {
      setCheckoutErrorPopupParams({
        title: t.get(
          'general.address_form.validation_errors.address.not_supported',
          { product: unsupportedProduct.name }
        ),
        buttonText: t.get('general.got_it'),
        onClick: () => {
          setCheckoutErrorPopupParams(null)
          analytics.track(EVENT_NAMES.ORDER_COUNTRY_RESTRICTION_POPUP_CLOSED, {
            Material: unsupportedProduct.material,
            Size: unsupportedProduct.size,
            Country: address.countryId,
            Reason: unsupportedProduct.type,
          })
        },
      })
      analytics.track(EVENT_NAMES.ORDER_COUNTRY_RESTRICTION_POPUP_SHOWN, {
        Material: unsupportedProduct.material,
        Size: unsupportedProduct.size,
        Country: address.countryId,
        Reason: unsupportedProduct.type,
      })
    }
    return false
  }

  function validatePlaceOrder(
    validateWithUserInteraction = true,
    isFromPaymentRequestButton = false
  ) {
    const isAddressMissing = isCheckoutAddressMissing() && !isGiftCard
    const isEmailMissing = !UserManager.getUserEmail()
    const paymentMethodType = PaymentManager.getSelectedPaymentMethodType()
    const pricingSummary = checkout.pricingSummary
    const isStalePayMethod = shouldTreatAsStalePaymentMethod({
      paymentMethodType,
      paymentMethodId: PaymentManager.getStripePaymentMethodId(),
      isFromPaymentRequestButton,
    })
    if (isStalePayMethod) {
      logger.info('Stale Stripe payment method detected', {
        action: 'blocked-at-submit',
        paymentMethodType,
      })
    }
    const isPaymentMissing =
      (!paymentMethodType || isStalePayMethod) && pricingSummary.totalPrice > 0

    if (!validateOrderHasItems(validateWithUserInteraction)) {
      return false
    }

    const isPrimaryInfoMissing = isGiftCard ? isEmailMissing : isAddressMissing

    const openPrimaryInfoDialog = (shouldShowPaymentAfter: boolean) => {
      if (isGiftCard) {
        setShowGiftCardEmailDialog(true)
        setShouldShowPaymentDialogAfterEmail(shouldShowPaymentAfter)
      } else {
        setShowAddressDialog(true)
        setShouldShowPaymentDialogAfterAddress(shouldShowPaymentAfter)
      }
    }

    // No primary info and no payment method
    if (isPrimaryInfoMissing && isPaymentMissing) {
      if (validateWithUserInteraction) {
        openPrimaryInfoDialog(true)
      }
      return false
    }

    // No payment method
    if (isPaymentMissing) {
      if (validateWithUserInteraction) {
        openPaymentDialog()
      }
      return false
    }

    // No primary info
    if (isPrimaryInfoMissing) {
      if (validateWithUserInteraction) {
        openPrimaryInfoDialog(false)
      }
      return false
    }

    if (!isGiftCard) {
      // Bad address
      if (!validateAddress(validateWithUserInteraction)) {
        return false
      }

      // No email
      if (isEmailMissing) {
        if (validateWithUserInteraction) {
          analytics.track(EVENT_NAMES.MISSING_EMAIL_SHOWN)
          setShowAddressDialog(true)
          setShouldShowPaymentDialogAfterAddress(false)
        }
        return false
      }
    }

    return validateOrderReadiness(validateWithUserInteraction)
  }

  function isCheckoutAddressMissing() {
    return !checkout.address || Object.keys(checkout.address).length === 0
  }

  function validateExpressCheckoutPlaceOrder() {
    if (!validateOrderHasItems(true)) {
      return false
    }
    if (isGiftCard && !UserManager.getUserEmail()) {
      setShowGiftCardEmailDialog(true)
      setShouldShowPaymentDialogAfterEmail(false)
      return false
    }
    if (!isGiftCard && isCheckoutAddressMissing()) {
      setShowAddressDialog(true)
      setShouldShowPaymentDialogAfterAddress(false)
      return false
    }
    if (!isGiftCard && !validateAddress(true)) {
      return false
    }
    return validateOrderReadiness(true)
  }

  function validateOrderHasItems(validateWithUserInteraction: boolean) {
    if (checkout.pricingSummary.totalItems > 0) {
      return true
    }
    validateWithUserInteraction &&
      openMissingInfoDialog(
        t.get('order.checkout.payment_errors.no_tiles_selected')
      )
    return false
  }

  function validateOrderReadiness(validateWithUserInteraction: boolean) {
    // Every book is validated before the verdict rather than stopping at the
    // first failure: each one carries the warning its own editor surfaces.
    const bookValidations = getOrderedBookStores().map((bookStore) =>
      bookStore.validateOrder(validateWithUserInteraction)
    )
    return bookValidations.every(
      (status) => status === PhotoBookOrderValidation.VALID
    )
  }

  const getPlaceOrderButtonText = () => {
    if (inGdprCountry()) {
      return t.get('order.checkout.confirm_button_text_gdpr')
    } else {
      return t.get('order.checkout.confirm_button_text_non_gdpr')
    }
  }

  function renderPayPalButton(isDisabled = false) {
    return (
      <PayPalButtonWrapper
        createOrder={async () => {
          const transactionId = await initiatePaypalPayment(
            checkout.pricingSummary,
            checkout.address,
            PaymentManager.getPayPalAccountName()
          )
          setTransactionId(transactionId)
          return transactionId
        }}
        onApprove={handlePlaceOrderClicked}
        style={{
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'pay',
          height: 52, // This should match the corresponding variable in the scss
        }}
        disabled={!validatePlaceOrder(false) || isDisabled}
        onDisabledClick={() => readOutsideReaction(() => validatePlaceOrder())}
      />
    )
  }

  function _getDisplayDescription() {
    const pricingSummary = checkout.pricingSummary
    if (isGiftCard) {
      return getGiftCardDescription(pricingSummary)
    } else {
      const { totalTiles, freeTiles } = pricingSummary
      const payedTiles = totalTiles - freeTiles
      return payedTiles > 1
        ? t.get('order.checkout.payment_summary_rows.many_tiles', {
            v1: payedTiles,
          })
        : t.get('order.checkout.payment_summary_rows.one_tile')
    }
  }

  function renderStripeButton(stripeButton: ReactNode, disableButton: boolean) {
    return (
      <div className="payment-request-button-container">
        {stripeButton}
        {disableButton && <div className="paypal-button-overlay" />}
      </div>
    )
  }

  function renderExpressCheckoutButton() {
    return (
      <ExpressCheckoutButton
        addressSource={ADDRESS_SOURCE.CHECKOUT_FORM}
        shouldBlockOpen={() =>
          !readOutsideReaction(() => validateExpressCheckoutPlaceOrder())
        }
        onOrderSubmitted={props.dismiss}
        callCreateOrder={props.callCreateOrder}
        isCartFlow={props.isCartFlow}
        entryPoint={CHECKOUT_ENTRY_POINT.CHECKOUT_SHEET}
      />
    )
  }

  function renderPaymentRequestButton(paymentMethodType: PaymentMethodType) {
    const pricingSummary = checkout.pricingSummary
    const displayItems = toStripeSheetRows(translateKey, pricingSummary, {
      productLabel: _getDisplayDescription(),
      format: PAYMENT_REQUEST_FORMAT,
    }).map(({ name, amount }) => ({ label: name, amount }))
    return (
      <Elements stripe={props.stripe} options={{ fonts: FONTS }}>
        <StripePaymentRequestForm
          paymentRequestData={props.paymentRequestData}
          orderPriceSummary={checkout.pricingSummary}
          paymentMethod={paymentMethodType}
          displayItems={displayItems}
          validate={() =>
            readOutsideReaction(() => validatePlaceOrder(true, true))
          }
          action={handlePlaceOrderClicked}
        />
      </Elements>
    )
  }

  function renderLoadingButton(shouldDisableButton: boolean) {
    return (
      <Button
        fullWidth
        disabled={shouldDisableButton}
        backgroundColor={theme.colors.grayDark}
        loading
      />
    )
  }

  function renderCreditCardButton(shouldDisableButton: boolean) {
    return (
      <Button
        noShrink
        fullWidth
        onClick={() => handlePlaceOrderClicked()}
        disabled={shouldDisableButton}
        testId="place-order-button"
      >
        {getPlaceOrderButtonText()}
      </Button>
    )
  }

  function renderPaymentButton(paymentMethodType: any, isFreeOrder: boolean) {
    const { isLoading, pricingSummary } = checkout
    const shouldDisableButton =
      isLoading ||
      (pricingSummary && pricingSummary?.totalItems === 0) ||
      !props.paymentMethodCheckInitiated

    if (!props.paymentMethodCheckInitiated) {
      return renderLoadingButton(shouldDisableButton)
    }

    if (isFreeOrder) {
      // If the user don't have to pay, show regular 'Place Order' button,
      // even if a payment method is chosen.
      return renderCreditCardButton(shouldDisableButton)
    }

    if (paymentMethodType === PaymentMethodType.PAYPAL) {
      return renderPayPalButton(shouldDisableButton)
    }

    if (
      isPaymentMethodGoogleOrApple(paymentMethodType) &&
      props.paymentRequestData
    ) {
      const isExpressCheckoutVariant = experimentManager.isEnabled(
        'express-checkout-payment-only'
      )
      if (!isExpressCheckoutVariant) {
        return renderStripeButton(
          renderPaymentRequestButton(paymentMethodType),
          shouldDisableButton
        )
      }
      if (!isWalletUnavailable) {
        return renderStripeButton(
          renderExpressCheckoutButton(),
          shouldDisableButton
        )
      }
    }

    return renderCreditCardButton(shouldDisableButton)
  }

  function renderShippingOption(
    deliveryPromiseOption: ShippingOption,
    isDefaultOption = true
  ) {
    const selectedDeliveryPromise = checkout.selectedShippingOption
    const checked = selectedDeliveryPromise === deliveryPromiseOption
    const shippingPricing = getShippingOptionPricing(deliveryPromiseOption)
    if (!shippingPricing) {
      logger.warning('No shipping pricing found', {
        deliveryPromiseOption,
        shippingOptionName: deliveryPromiseOption?.shippingOptionName,
      })
      return null
    }

    const { cost, currencyCode } = shippingPricing

    const freeShippingTitle = t.get('order.checkout.shipping_options.standard')
    const formattedPrice = formatPrice(cost, currencyCode)
    const title = getShippingTitle(
      deliveryPromiseOption,
      freeShippingTitle,
      formattedPrice
    )
    const freeShippingOption = checkout.shippingOptions.find(
      (option) => option.shippingOptionName === SHIPPING_OPTION_FREE
    )
    const arrivesBeforeHolidayText =
      checkout.hasUpToDateDeliveryDates &&
      getEncourageExpressShippingForHolidayText({
        shippingOption: deliveryPromiseOption,
        freeShippingOption,
        country: countryManager.getCountry(),
      })
    const separateProductArrivals = getSeparateProductArrivals(
      deliveryPromiseOption
    )

    return (
      <S.ShippingOptionContainer key={deliveryPromiseOption.shippingOptionName}>
        <RadioButton
          className="shipping-option"
          value={deliveryPromiseOption.shippingOptionName}
          selectedValue={checkout.selectedShippingOptionName}
          onChange={() =>
            handleSelectDeliveryPromise(
              deliveryPromiseOption.shippingOptionName
            )
          }
          data-testid={`${deliveryPromiseOption.shippingOptionName}-shipping-type-button`}
        >
          <div className="message">
            <div
              className={classNames('title', { bold: checked })}
              data-testid={`${deliveryPromiseOption.shippingOptionName}-shipping-type`}
            >
              {title}
            </div>
            {checkout.hasUpToDateDeliveryDates && (
              <div
                className="description"
                data-testid={`${deliveryPromiseOption.shippingOptionName}-shipping-date`}
              >
                {separateProductArrivals ? (
                  <ProductArrivalLines {...separateProductArrivals} />
                ) : (
                  getDeliveryDateDescription(
                    translateKey,
                    deliveryPromiseOption,
                    isDefaultOption
                  )
                )}
              </div>
            )}
            {checkout.isCalculatingDeliveryDates && (
              <div
                className="description"
                data-testid={`${deliveryPromiseOption.shippingOptionName}-shipping-date-calculating`}
              >
                {t.get(CALCULATING_DELIVERY_DATE_KEY)}
              </div>
            )}
          </div>
        </RadioButton>
        {arrivesBeforeHolidayText && (
          <S.ArrivesBeforeHolidayBadge>
            {arrivesBeforeHolidayText}
          </S.ArrivesBeforeHolidayBadge>
        )}
      </S.ShippingOptionContainer>
    )
  }

  function getSeparateProductArrivals(shippingOption: ShippingOption) {
    const productArrivals = getProductArrivals(shippingOption)
    if (
      !productArrivals ||
      !shouldShowSeparateProductArrivals({
        productArrivals,
        country: countryManager.getCountry(),
      })
    ) {
      return undefined
    }
    return productArrivals
  }

  function getSelectedDeliveryDescription(
    selectedShippingOption: ShippingOption
  ) {
    const separateProductArrivals = getSeparateProductArrivals(
      selectedShippingOption
    )
    if (!separateProductArrivals) {
      return getDeliveryDateDescription(translateKey, selectedShippingOption)
    }
    const { tilesArrivalText, photoBookArrivalText } = getProductArrivalTexts(
      translateKey,
      separateProductArrivals
    )
    // SummaryItem renders its text with white-space: break-spaces, so each product gets its own line
    return [
      t.get(SHIPPING_ROW_KEY),
      tilesArrivalText,
      photoBookArrivalText,
    ].join('\n')
  }

  function getSummaryDeliveryDescription() {
    const { selectedShippingOption } = checkout
    if (_shippingOptionsEnabled() || !selectedShippingOption) {
      return t.get(SHIPPING_ROW_KEY)
    }
    if (checkout.hasUpToDateDeliveryDates) {
      return getSelectedDeliveryDescription(selectedShippingOption)
    }
    if (checkout.isCalculatingDeliveryDates) {
      return t.get(CALCULATING_DELIVERY_DATE_KEY)
    }
    return t.get(SHIPPING_ROW_KEY)
  }

  function getShippingTitle(
    deliveryPromiseOption: ShippingOption,
    freeShippingTitle: string,
    formattedPrice: string
  ) {
    let title = ''
    switch (deliveryPromiseOption.shippingOptionName) {
      case SHIPPING_OPTION_FREE:
        title = freeShippingTitle
        break
      case SHIPPING_OPTION_EXPRESS:
        title = t.get('order.checkout.shipping_options.express_new_version', {
          v1: formattedPrice,
        })
        break
      case SHIPPING_OPTION_PRIORITY:
        title = t.get('order.checkout.shipping_options.priority', {
          v1: formattedPrice,
        })
        break
      default:
        logger.error('Unknown shipping option', null, {
          shippingOptionName: deliveryPromiseOption.shippingOptionName,
        })
    }
    return title
  }

  const handleSelectDeliveryPromise = (
    shippingOptionName: ShippingOptionName
  ) => {
    checkout.setSelectedShippingOptionName(shippingOptionName)
  }

  const openAddGiftNoteFlow = () => {
    analytics.track('Add/Edit Gift Note Tapped')
    setShowGiftNote(true)
  }

  const closeGiftNote = () => {
    setShowGiftNote(false)
  }

  const _isPhotoBookOnlyOrder = () =>
    props.isCartFlow &&
    cart.cartItems.length > 0 &&
    cart.cartItems.every(
      (item) => item.productType === PRODUCT_TYPES.PHOTO_BOOK
    )

  const _giftNotesEnabled = () => {
    return (
      countryManager.getCountry() === 'US' &&
      (!_isPhotoBookOnlyOrder() ||
        UserManager.getUserEmail()
          ?.trim()
          .toLowerCase()
          .endsWith(PHOTO_BOOK_GIFT_NOTE_EMAIL_SUFFIX)) &&
      ![PRODUCT_TYPES.GIFT_CARD, PRODUCT_TYPES.BUSINESS].includes(productType)
    )
  }

  const _shippingOptionsEnabled = () => {
    const { shippingOptions, selectedShippingOption } = checkout
    return (
      selectedShippingOption &&
      shippingOptions &&
      shippingOptions.length > 1 &&
      !isGiftCard
    )
  }

  const handleSubmitShippingAddress = async (
    address: CheckoutContextValue['address'],
    email?: string
  ) => {
    await checkout.setAddress(address)
    if (email) {
      await UserManager.setUserEmail({
        email,
        source: CAPTURE_EMAIL_SOURCE.checkoutAddress,
        refetchExperiments: false,
        onboardingFlow: undefined,
        emailConsent: UserManager.hasExplicitEmailConsent(email),
      })
    }
    analytics.track(EVENT_NAMES.CHECKOUT_SHIPPING_ADDRESS_ADDED, address)
    setAddress(address)
  }

  const handleSubmitGiftCardEmail = async (email: string) => {
    analytics.track(EVENT_NAMES.CHECKOUT_GIFT_CARD_EMAIL_ADDED, { email })
    setGiftCardBuyerEmail(email)
    await UserManager.setUserEmail({
      email,
      source: CAPTURE_EMAIL_SOURCE.checkoutGiftCard,
      refetchExperiments: false,
      onboardingFlow: undefined,
      emailConsent: UserManager.hasExplicitEmailConsent(email),
    })
  }

  function _getPaymentMethodText(
    paymentMethodType: PaymentMethodType | null = null
  ) {
    if (!paymentMethodType) {
      return t.get('order.checkout.add_payment')
    } else if (paymentMethodType === PaymentMethodType.CREDIT_CARD) {
      const creditCard = PaymentManager.getSelectedCard()

      if (creditCard) {
        // Solves a bug where CREDIT_CARD is set but no credit card was added
        return `${capitalize(creditCard.brand)} (${t
          .get('order.checkout.add_payment_options.credit_card_ends_with', {
            v1: creditCard.last4,
          })
          .toLowerCase()})`
      } else {
        return t.get('order.checkout.add_payment')
      }
    } else {
      return t.get('order.checkout.add_payment_options.paying_with', {
        v1: PaymentMethodTypeDisplay[paymentMethodType],
      })
    }
  }

  function renderCheckoutSheet(shouldMakeCheckoutFullHeight: boolean) {
    const { address, shippingOptions, selectedShippingOption } = checkout
    // The treatment's bag asks for dates by the IP location, so its address form starts there too.
    const initialAddress =
      address ??
      (props.isCartFlow && experimentManager.isEnabled('cart-holiday-cutoffs')
        ? getIpLocationAddress({ ipCountry, ipState })
        : undefined)
    let paymentMethodType = PaymentManager.getSelectedPaymentMethodType()
    if (
      paymentMethodType === PaymentMethodType.APPLE_PAY &&
      !props.canMakeApplePay
    ) {
      paymentMethodType = null
    }

    const addressText =
      address == null
        ? t.get('order.checkout.add_address')
        : `${address.fullName}, ${
            address.city || COUNTRIES[address.countryId].name
          }`
    const giftCardEmailText =
      giftCardBuyerEmail == null
        ? t.get('gift_card.form.add_email_address')
        : giftCardBuyerEmail
    const paymentMethodText = _getPaymentMethodText(paymentMethodType)
    const pricingSummary = checkout.pricingSummary
    const isFreeOrder =
      pricingSummary.totalItems > 0 &&
      pricingSummary.totalPrice === 0 &&
      !isGiftCardOrder(pricingSummary.productType)
    const giftNote = giftNoteManager.getGiftNote()
    const closeButton = (
      <CloseButton
        action={props.dismiss}
        dark
        dataTestId="checkout-context-close-button"
      />
    )

    return (
      <>
        <BrowserThemeColor color={theme.colors.drawerGray} />
        <div
          className={classNames('checkout-content', {
            'right-drawer': props.rightDrawer,
            'full-height': shouldMakeCheckoutFullHeight,
          })}
        >
          {props.desktopReviewElegant && props.rightDrawer && closeButton}
          {isGiftCard ? (
            <SheetButton
              className="with-lower-text"
              icon={<S.EmailIcon />}
              action={openGiftCardEmailDialog}
              highlighted={giftCardBuyerEmail == null}
              showChangeButton={giftCardBuyerEmail !== null}
              testId="add-gift-card-email-button"
            >
              {giftCardEmailText}
            </SheetButton>
          ) : (
            <SheetButton
              className="with-lower-text"
              icon={<AddressIcon className="icon-image" />}
              action={openAddressDialog}
              highlighted={address == null}
              testId="add-address-button"
              showChangeButton={address !== null}
            >
              {addressText}
            </SheetButton>
          )}

          {_shippingOptionsEnabled() && (
            <div className="shipping-options">
              {shippingOptions.map((option) =>
                renderShippingOption(option, shippingOptions.length === 1)
              )}
            </div>
          )}

          {
            // If user doesn't need to pay for anything, we don't want to show
            // payment selection dialog.
            !isFreeOrder && (
              <SheetButton
                icon={<CreditCardIcon className="icon-image" />}
                action={openPaymentDialog}
                highlighted={paymentMethodType == null}
                testId="add-payment-method-button"
                showChangeButton={paymentMethodType !== null}
              >
                {paymentMethodText}
              </SheetButton>
            )
          }

          {_giftNotesEnabled() && (
            <SheetButton
              className="with-lower-text"
              icon={<GiftIcon className="icon-image" />}
              action={openAddGiftNoteFlow}
              testId="add-gift-note-button"
            >
              {t.get(
                giftNote
                  ? 'order.checkout.gift_note_added'
                  : 'order.checkout.add_gift_note'
              )}
            </SheetButton>
          )}

          <GiftNoteDialog open={showGiftNote} onClose={closeGiftNote} />

          <OrderSummary
            tiles={checkout.orderTiles}
            hasAddress={!!address}
            data={checkout.pricingSummary}
            deliveryPromise={selectedShippingOption}
            deliveryDescription={getSummaryDeliveryDescription()}
            cancelPromo={props.openCancelPromoCodeDialog}
            onUpdateProductQuantity={checkout.updateProductQuantity}
            openPromoCodeDialog={openPromoCodeDialog}
            closePromoCodeDialog={closePromoCodeDialog}
            isCartFlow={props.isCartFlow}
          />

          {renderPaymentButton(paymentMethodType, isFreeOrder)}
          {countryManager.getCountry() === 'JP' && (
            <S.TokushohoDisclaimer href={TOKUSHOHO_URL}>
              {t.get('order.checkout.tokushoho_disclaimer')}
            </S.TokushohoDisclaimer>
          )}

          {isGiftCard ? (
            <MixtilesDialog
              open={showGiftCardEmailDialog}
              onDismiss={() => handleCloseGiftCardEmailDialog(false)}
            >
              <GiftCardEmailForm
                onClose={handleCloseGiftCardEmailDialog}
                onSubmit={handleSubmitGiftCardEmail}
                initialEmail={giftCardBuyerEmail}
              />
            </MixtilesDialog>
          ) : (
            <MixtilesDialog open={showAddressDialog}>
              <AddressForm
                initialAddress={initialAddress}
                onClose={handleCloseAddressDialog}
                showPhoneError={showPhoneError}
                onSubmitShippingAddress={(address) =>
                  handleSubmitShippingAddress(address)
                }
              />
            </MixtilesDialog>
          )}

          <MixtilesDialog
            open={showPaymentElementDialog}
            keepMounted={paymentMethodType === null} // Preload Stripe's elements if no credit card was added yet
          >
            {showPaymentElementDialog && (
              <StripePaymentForm
                onDone={(args) => handlePaymentSubmit(args)}
                onClose={handleCreditCardDismissed}
                shippingAddress={address}
                onSubmitShippingAddress={(address, email) =>
                  handleSubmitShippingAddress(address, email)
                }
                shouldRenderPaymentElement={
                  !checkout.isLoading && checkout.pricingSummary?.totalPrice > 0
                }
              />
            )}
          </MixtilesDialog>

          <MixtilesDialog
            fullScreen={false}
            open={showMissingInfoDialog}
            onDismiss={handleCloseMissingInfoDialog}
            transition="grow"
          >
            <BasicDialogContent
              title={missingInfoMessage?.title}
              text={missingInfoMessage?.text}
            />
            <BasicDialogButton action={() => handleCloseMissingInfoDialog()}>
              {t.get('general.ok')}
            </BasicDialogButton>
          </MixtilesDialog>
          {checkoutErrorPopupParams && (
            <AlertPopup open {...checkoutErrorPopupParams} />
          )}
        </div>
      </>
    )
  }

  return (
    <>
      <div
        className={classNames('checkout-sheet', {
          'right-drawer': props.rightDrawer,
        })}
      >
        <div className="checkout-title">
          {t.get('order.checkout_button_text')}
        </div>
        {renderCheckoutSheet(false)}
      </div>
    </>
  )
}

export default observer(CheckoutSheet)
